import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { DEFAULT_SETTINGS, type Settings } from '@core/settings/schema.ts';
import {
  STYLE_PRESETS,
  applyTrackStyleToSettings,
  trackStyleFromSettings,
  type StylePresetId,
  type TrackStyle,
} from '@core/styles/stylePresets.ts';
import {
  MAX_CUSTOM_STYLE_PRESETS,
  createCustomStylePresetId,
  nextDefaultCustomStyleName,
  normalizeCustomStylePresets,
  type CustomStylePreset,
} from '@core/styles/customStylePresets.ts';
import {
  GOOGLE_FONT_GRID_PAGE_SIZE,
  GOOGLE_FONT_SUBSET_OPTIONS,
  catalogEntryToFontFields,
  filterGoogleFonts,
  formatGoogleFontSubsetLabel,
  getGoogleFontsCatalog,
  listGoogleFontSubsets,
  loadGoogleFontsCatalog,
  pageGoogleFonts,
  previewForGoogleFontSubset,
  type GoogleFontCatalogEntry,
} from '@core/styles/googleFontsCatalog.ts';
import {
  buildStyleStudioLanguageOptions,
  filterAndOrderStyleLanguageOptions,
} from '@core/styles/styleStudioLanguageCatalog.ts';
import {
  findLanguageOwner,
  languagesForPreset,
  normalizeStyleLanguagePriorities,
  setLanguagePriority,
} from '@core/styles/styleLanguagePriorities.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import azureJson from '../../../extension/src/adapters/resource/json/azure.json';
import {
  StyleLookPreview,
  StylePresetThumb,
} from '../components/StyleLookPreview.tsx';
import { useI18n } from '../i18n/index.ts';

export type StyleStudioPageProps = {
  settingsStore: SettingsStore;
  onBack: () => void;
};

function stylesEqual(a: TrackStyle, b: TrackStyle): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

const LANG_OPTIONS = buildStyleStudioLanguageOptions(
  azureJson.translation as Record<string, { name: string; nativeName: string }>,
);

/**
 * Mobile Style Studio — presets, custom save, Google Fonts, language→style bindings.
 * Selection updates a draft + preview only; Apply commits to live track styles
 * (aligned with extension Style Studio).
 */
export function StyleStudioPage({ settingsStore, onBack }: StyleStudioPageProps) {
  const { t, presetLabel } = useI18n();
  const settings = useSyncExternalStore(
    (cb) => settingsStore.subscribe(cb),
    () => settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS,
  );

  const [customName, setCustomName] = useState('');
  const [scope, setScope] = useState<'main' | 'second' | 'both'>('both');
  const [fontQuery, setFontQuery] = useState('');
  const [googleSubset, setGoogleSubset] = useState('');
  const [googleFontLimit, setGoogleFontLimit] = useState(GOOGLE_FONT_GRID_PAGE_SIZE);
  const [fontCatalogReady, setFontCatalogReady] = useState(
    () => getGoogleFontsCatalog().length > 0,
  );
  const [selectedCustomId, setSelectedCustomId] = useState<string | null>(null);
  const [langQuery, setLangQuery] = useState('');
  const [bindError, setBindError] = useState('');
  const [applyStatus, setApplyStatus] = useState('');
  const [draftStyle, setDraftStyle] = useState<TrackStyle>(() =>
    trackStyleFromSettings(settings, 'main'),
  );

  const customs = useMemo(
    () => normalizeCustomStylePresets(settings.customStylePresets),
    [settings.customStylePresets],
  );

  const priorities = useMemo(
    () => normalizeStyleLanguagePriorities(settings.styleLanguagePriorities, customs),
    [settings.styleLanguagePriorities, customs],
  );

  const previewSettings = useMemo(
    () => ({ ...settings, ...applyTrackStyleToSettings(draftStyle, scope) }),
    [settings, draftStyle, scope],
  );

  useEffect(() => {
    if (fontCatalogReady) return;
    void loadGoogleFontsCatalog(fetch).then((ok) => {
      if (ok) setFontCatalogReady(true);
    });
  }, [fontCatalogReady]);

  useEffect(() => {
    if (selectedCustomId && !customs.some((p) => p.id === selectedCustomId)) {
      setSelectedCustomId(customs[0]?.id ?? null);
    }
  }, [customs, selectedCustomId]);

  useEffect(() => {
    setGoogleFontLimit(GOOGLE_FONT_GRID_PAGE_SIZE);
  }, [googleSubset, fontQuery, fontCatalogReady]);

  const subsetOptions = useMemo(() => {
    const available = new Set(listGoogleFontSubsets());
    return GOOGLE_FONT_SUBSET_OPTIONS.filter((opt) => available.has(opt.subset));
  }, [fontCatalogReady]);

  const googleFilterReady = Boolean(googleSubset.trim() || fontQuery.trim());

  const filteredFonts = useMemo(() => {
    if (!googleFilterReady) return [];
    return filterGoogleFonts({
      subset: googleSubset || null,
      query: fontQuery,
    });
  }, [googleFilterReady, googleSubset, fontQuery, fontCatalogReady]);

  const pagedFonts = useMemo(
    () => pageGoogleFonts(filteredFonts, googleFontLimit),
    [filteredFonts, googleFontLimit],
  );

  const previewCopy = useMemo(
    () => previewForGoogleFontSubset(googleSubset || null),
    [googleSubset],
  );

  const patch = (partial: Partial<Settings>) => {
    void settingsStore.set(partial);
  };

  /** Select into draft only — does not write live track styles. */
  const selectStyle = (style: TrackStyle) => {
    setDraftStyle(style);
    setApplyStatus('');
  };

  const selectBuiltin = (id: StylePresetId) => {
    const preset = STYLE_PRESETS.find((p) => p.id === id);
    if (!preset) return;
    setSelectedCustomId(null);
    selectStyle(preset.style);
  };

  const selectCustom = (preset: CustomStylePreset) => {
    setSelectedCustomId(preset.id);
    selectStyle(preset.style);
  };

  const applyDraft = () => {
    patch(applyTrackStyleToSettings(draftStyle, scope));
    setApplyStatus(t('styleStudioApplied'));
  };

  const saveCustom = () => {
    if (customs.length >= MAX_CUSTOM_STYLE_PRESETS) return;
    const style = draftStyle;
    const name =
      customName.trim() || nextDefaultCustomStyleName(customs, 'My style');
    const next: CustomStylePreset = {
      id: createCustomStylePresetId(),
      name,
      style,
      preview: { main: 'Hello', second: '你好', thumb: 'Aa' },
    };
    patch({ customStylePresets: [...customs, next] });
    setCustomName('');
    setSelectedCustomId(next.id);
  };

  const deleteCustom = (id: string) => {
    const nextCustoms = customs.filter((p) => p.id !== id);
    patch({
      customStylePresets: nextCustoms,
      styleLanguagePriorities: normalizeStyleLanguagePriorities(priorities, nextCustoms),
    });
    if (selectedCustomId === id) {
      setSelectedCustomId(nextCustoms[0]?.id ?? null);
    }
  };

  const selectGoogleFont = (entry: GoogleFontCatalogEntry | null) => {
    const fields = entry
      ? catalogEntryToFontFields(entry)
      : { fontGoogleFamilies: '', fontGoogleCssProperties: '' };
    const next: TrackStyle = {
      ...draftStyle,
      ...fields,
      fontFamily: entry?.fallback || (entry ? draftStyle.fontFamily : ''),
    };
    setSelectedCustomId(null);
    selectStyle(next);
  };

  const toggleLanguageBinding = (matchKey: string, checked: boolean) => {
    if (!selectedCustomId) return;
    if (checked) {
      const owner = findLanguageOwner(priorities, customs, matchKey, selectedCustomId);
      if (owner) {
        setBindError(t('styleStudioLangConflict').replace('{name}', owner.name));
        return;
      }
    }
    setBindError('');
    patch({
      styleLanguagePriorities: setLanguagePriority(
        priorities,
        matchKey,
        checked ? selectedCustomId : null,
      ),
    });
  };

  const draftMatches = (style: TrackStyle) => stylesEqual(style, draftStyle);
  const currentFamilies = draftStyle.fontGoogleFamilies;

  const selectedCustom = selectedCustomId
    ? customs.find((p) => p.id === selectedCustomId)
    : undefined;

  const selectedLangKeys = useMemo(
    () => new Set(selectedCustomId ? languagesForPreset(priorities, selectedCustomId) : []),
    [priorities, selectedCustomId],
  );

  const langOptions = useMemo(
    () => filterAndOrderStyleLanguageOptions(LANG_OPTIONS, selectedLangKeys, langQuery),
    [selectedLangKeys, langQuery],
  );

  return (
    <main className="style-studio" data-testid="style-studio">
      <header className="style-studio__header">
        <button
          type="button"
          className="btn"
          data-testid="style-studio-back"
          onClick={onBack}
        >
          {t('settingsClose')}
        </button>
        <h1>{t('styleStudioTitle')}</h1>
        <button
          type="button"
          className="btn style-studio__apply"
          data-testid="style-studio-apply"
          onClick={applyDraft}
        >
          {t('settingsApplyPreset')}
        </button>
      </header>

      {applyStatus ? (
        <p className="style-studio__status" role="status" data-testid="style-studio-apply-status">
          {applyStatus}
        </p>
      ) : null}

      <section className="style-studio__section">
        <h2>{t('settingsStylePreview')}</h2>
        <StyleLookPreview
          settings={previewSettings}
          previewMain={previewCopy.main}
          previewSecond={previewCopy.second}
        />
      </section>

      <section className="style-studio__section">
        <h2>{t('styleStudioApplyScope')}</h2>
        <div className="segmented" role="group" data-testid="style-studio-scope">
          {(['main', 'second', 'both'] as const).map((id) => (
            <button
              key={id}
              type="button"
              className={`segmented__btn${scope === id ? ' segmented__btn--active' : ''}`}
              data-testid={`style-studio-scope-${id}`}
              onClick={() => setScope(id)}
            >
              {id === 'both'
                ? t('styleStudioScopeBoth')
                : id === 'main'
                  ? t('styleStudioScopeMain')
                  : t('styleStudioScopeSecond')}
            </button>
          ))}
        </div>
      </section>

      <section className="style-studio__section">
        <h2>{t('settingsPresets')}</h2>
        <div className="style-studio__presets" data-testid="style-studio-presets">
          {STYLE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`style-studio__preset${draftMatches(preset.style) ? ' style-studio__preset--active' : ''}`}
              data-testid={`style-studio-preset-${preset.id}`}
              onClick={() => selectBuiltin(preset.id)}
            >
              <StylePresetThumb
                style={preset.style}
                sample={preset.preview?.thumb ?? 'Aa'}
              />
              <span>{presetLabel(preset.id)}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="style-studio__section">
        <h2>{t('styleStudioGoogleFonts')}</h2>
        <p className="style-studio__hint">
          {currentFamilies
            ? `${t('styleStudioGoogleCurrent')}: ${currentFamilies}`
            : t('styleStudioGoogleNone')}
        </p>
        <label className="style-studio__field-label">
          <span>{t('styleStudioGoogleSubset')}</span>
          <select
            className="field"
            data-testid="style-studio-font-subset"
            value={googleSubset}
            disabled={!fontCatalogReady}
            onChange={(e) => setGoogleSubset(e.target.value)}
          >
            <option value="">{t('styleStudioGoogleSubsetAll')}</option>
            {subsetOptions.map((opt) => (
              <option key={opt.subset} value={opt.subset}>
                {formatGoogleFontSubsetLabel(opt.en, opt.nativeName)}
              </option>
            ))}
          </select>
        </label>
        <input
          className="style-studio__font-search"
          data-testid="style-studio-font-search"
          type="search"
          value={fontQuery}
          placeholder={t('styleStudioGoogleSearch')}
          onChange={(e) => setFontQuery(e.target.value)}
        />
        {!fontCatalogReady ? (
          <p className="style-studio__hint">{t('styleStudioGoogleLoading')}</p>
        ) : !googleFilterReady ? (
          <p className="style-studio__hint" data-testid="style-studio-font-need-filter">
            {t('styleStudioGoogleNeedFilter')}
          </p>
        ) : pagedFonts.total === 0 ? (
          <p className="style-studio__hint">{t('styleStudioGoogleSearch')}</p>
        ) : (
          <>
            <div className="style-studio__font-grid" data-testid="style-studio-font-grid">
              {pagedFonts.visible.map((font) => (
                <button
                  key={font.id}
                  type="button"
                  className={`style-studio__font-card${
                    draftStyle.fontGoogleFamilies === font.familiesParam
                      ? ' style-studio__font-card--active'
                      : ''
                  }`}
                  data-testid={`style-studio-font-${font.id}`}
                  onClick={() => selectGoogleFont(font)}
                >
                  <span className="style-studio__font-card-name">{font.family}</span>
                  <span className="style-studio__font-card-sample">{previewCopy.thumb}</span>
                </button>
              ))}
            </div>
            {pagedFonts.hasMore ? (
              <button
                type="button"
                className="btn style-studio__font-load-more"
                data-testid="style-studio-font-load-more"
                onClick={() => setGoogleFontLimit((n) => n + GOOGLE_FONT_GRID_PAGE_SIZE)}
              >
                {t('styleStudioGoogleLoadMore')}
              </button>
            ) : null}
          </>
        )}
        <button
          type="button"
          className="btn"
          data-testid="style-studio-font-clear"
          onClick={() => selectGoogleFont(null)}
        >
          {t('styleStudioGoogleClear')}
        </button>
      </section>

      <section className="style-studio__section">
        <h2>{t('styleStudioCustoms')}</h2>
        <div className="style-studio__save-row">
          <input
            data-testid="style-studio-custom-name"
            type="text"
            value={customName}
            placeholder={t('styleStudioCustomName')}
            onChange={(e) => setCustomName(e.target.value)}
          />
          <button
            type="button"
            className="btn"
            data-testid="style-studio-save-custom"
            disabled={customs.length >= MAX_CUSTOM_STYLE_PRESETS}
            onClick={saveCustom}
          >
            {t('styleStudioSave')}
          </button>
        </div>
        {customs.length === 0 ? (
          <p className="style-studio__hint">{t('styleStudioCustomsEmpty')}</p>
        ) : (
          <ul className="style-studio__customs" data-testid="style-studio-customs">
            {customs.map((preset) => (
              <li
                key={preset.id}
                className={`style-studio__custom${
                  selectedCustomId === preset.id ? ' style-studio__custom--selected' : ''
                }`}
              >
                <button
                  type="button"
                  className="style-studio__custom-apply"
                  data-testid={`style-studio-custom-${preset.id}`}
                  onClick={() => selectCustom(preset)}
                >
                  <StylePresetThumb
                    style={preset.style}
                    sample={preset.preview?.thumb ?? 'Aa'}
                  />
                  <span>{preset.name}</span>
                </button>
                <button
                  type="button"
                  className="btn"
                  data-testid={`style-studio-delete-${preset.id}`}
                  onClick={() => deleteCustom(preset.id)}
                >
                  {t('styleStudioDelete')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="style-studio__section">
        <h2>{t('styleStudioLangBind')}</h2>
        <p className="style-studio__hint">{t('styleStudioLangBindHint')}</p>
        {customs.length === 0 ? (
          <p className="style-studio__hint">{t('styleStudioLangBindNeedCustom')}</p>
        ) : !selectedCustom ? (
          <p className="style-studio__hint">{t('styleStudioLangBindSelectCustom')}</p>
        ) : (
          <>
            <p className="style-studio__hint" data-testid="style-studio-bind-target">
              {selectedCustom.name}
            </p>
            <input
              className="style-studio__font-search"
              data-testid="style-studio-lang-search"
              type="search"
              value={langQuery}
              placeholder={t('styleStudioLangSearch')}
              onChange={(e) => setLangQuery(e.target.value)}
            />
            {bindError ? (
              <p
                className="style-studio__error"
                role="alert"
                data-testid="style-studio-bind-error"
              >
                {bindError}
              </p>
            ) : null}
            <ul className="style-studio__lang-list" data-testid="style-studio-lang-list">
              {langOptions.map((opt) => {
                const owner = findLanguageOwner(
                  priorities,
                  customs,
                  opt.matchKey,
                  selectedCustomId!,
                );
                const checked = selectedLangKeys.has(opt.matchKey);
                return (
                  <li key={opt.matchKey}>
                    <label
                      className={`style-studio__lang-item${owner ? ' style-studio__lang-item--conflict' : ''}${
                        checked ? ' style-studio__lang-item--checked' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        data-testid={`style-studio-lang-${opt.matchKey}`}
                        checked={checked}
                        disabled={Boolean(owner)}
                        onChange={(e) =>
                          toggleLanguageBinding(opt.matchKey, e.target.checked)
                        }
                      />
                      <span>
                        {owner
                          ? `${opt.label} — ${t('styleStudioLangConflict').replace('{name}', owner.name)}`
                          : opt.label}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>
    </main>
  );
}
